/**
 * Qualifier picker — ALPACA's `Qualifiers_ListView_DataPackage` popup (opened
 * from the Check Points Qualifier cell, DataPackageViewController.cs:1437-1814).
 *
 * Lists the Qualifiers lookup (QualifierID · Symbol · Definition) with, matching
 * legacy ALPACA: a top-right search, a select-all header checkbox, per-column
 * Excel-style filters, MULTI-SELECT rows, and a bottom pagination bar (page
 * count + page-size).  Pre-selects the qualifiers already in the cell (by
 * Symbol).  On OK, returns the selected Symbols comma-joined — exactly what
 * ALPACA stores in DataPackageChecklistQC.Qualifier.
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { compareQualifierSymbols } from './sdmsQualifierDropdown';
import {
  ColFilterButton, passesColumnFilter, cascadeRows, type ColumnFilterValue,
} from '../../components/ColumnFilterDropdown';
import './SdmsQualifierPicker.css';

interface Qualifier { qualifierId: string; symbol: string; definition: string; }

const COLS = [
  { key: 'qualifierId', label: 'QualifierID', width: 120, text: (r: Qualifier) => r.qualifierId ?? '' },
  { key: 'symbol',      label: 'Symbol',      width: 130, text: (r: Qualifier) => r.symbol ?? '' },
  { key: 'definition',  label: 'Definition',  width: 0,   text: (r: Qualifier) => r.definition ?? '' },
] as const;

const PAGE_SIZES = [10, 20, 50, 100];

export function SdmsQualifierPicker({ initial, onOk, onCancel }: {
  initial: string;                         // current cell value (comma-joined Symbols)
  onOk: (symbols: string) => void;
  onCancel: () => void;
}) {
  const [rows, setRows]       = useState<Qualifier[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState<string | null>(null);
  const [search, setSearch]   = useState('');
  // Pre-select whatever Symbols the cell already holds (comma-split).
  const [selected, setSelected] = useState<Set<string>>(() =>
    new Set(initial.split(',').map(s => s.trim()).filter(Boolean)));
  const [filters, setFilters] = useState<Record<string, ColumnFilterValue>>({});
  const [page, setPage]       = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/api/v1/analysis/qualifiers');
        // OG Qualifiers_ListView_SDMS sorts by Symbol ascending (SortIndex=0); the feed is
        // QualifierID-ordered, so sort here with the SAME comparator the sheet dropdown uses.
        const list = ((res.data as { data: Qualifier[] }).data ?? []).slice()
          .sort((a, b) => compareQualifierSymbols(a.symbol ?? '', b.symbol ?? ''));
        if (alive) setRows(list);
      } catch {
        if (alive) setErr('Failed to load qualifiers — check the API logs.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const textOf = (r: Qualifier, key: string) => COLS.find(c => c.key === key)?.text(r) ?? '';

  // Free-text search across all three columns.
  const searchFiltered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r => COLS.some(c => c.text(r).toLowerCase().includes(t)));
  }, [rows, search]);

  // …then the per-column Excel-style filters.
  const filtered = useMemo(() =>
    searchFiltered.filter(r => COLS.every(c => passesColumnFilter(filters[c.key] ?? null, c.text(r)))),
    [searchFiltered, filters]);

  // Any search / filter / page-size change resets to page 1.
  useEffect(() => { setPage(1); }, [search, filters, pageSize]);

  const totalPages   = Math.max(1, Math.ceil(filtered.length / pageSize));
  const clampedPage  = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => filtered.slice((clampedPage - 1) * pageSize, clampedPage * pageSize),
    [filtered, clampedPage, pageSize]);

  const toggle = (sym: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(sym)) next.delete(sym); else next.add(sym);
    return next;
  });

  // Select-all toggles every row in the CURRENT filtered set (across pages).
  const allSelected = filtered.length > 0 && filtered.every(r => selected.has(r.symbol));
  const toggleAll = () => setSelected(prev => {
    const next = new Set(prev);
    if (allSelected) filtered.forEach(r => next.delete(r.symbol));
    else             filtered.forEach(r => next.add(r.symbol));
    return next;
  });

  // Emit Symbols in the lookup's own order (stable), COMMA-joined with no space — exactly the
  // text OG writes into the Qualifier cell (Dc_AcceptingQualifier :10480: `"," + Symbol`), so
  // the persisted SampleInfo.Qualifier matches OG's byte for byte. An empty pick emits '' and
  // CLEARS the cell, as OG does. (was ', ' — 2026-09-22)
  const accept = () => onOk(rows.filter(r => selected.has(r.symbol)).map(r => r.symbol).join(','));

  return (
    <div className="qp-ov" role="dialog" aria-modal="true" aria-label="Qualifiers"
      onMouseDown={onCancel}>
      <div className="qp-box" onMouseDown={e => e.stopPropagation()}>
        <div className="qp-hd">
          <span>Qualifiers</span>
          <button className="qp-x" onClick={onCancel} aria-label="Close">×</button>
        </div>

        {/* Toolbar — search on the RIGHT (ALPACA parity). */}
        <div className="qp-tools">
          <span className="qp-sel-count">{selected.size} selected</span>
          <span style={{ flex: 1 }} />
          <div className="qp-search">
            <i className="ti ti-search" />
            <input placeholder="Search…" value={search} autoFocus
              onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        <div className="qp-grid">
          {loading ? (
            <div className="qp-msg">Loading…</div>
          ) : err ? (
            <div className="qp-msg" style={{ color: '#c0392b' }}>{err}</div>
          ) : (
            <table>
              <thead>
                <tr>
                  {/* Select-all header checkbox. */}
                  <th style={{ width: 34 }}>
                    <input type="checkbox" checked={allSelected} onChange={toggleAll}
                      aria-label="Select all" />
                  </th>
                  {COLS.map(col => (
                    <th key={col.key} style={col.width ? { width: col.width } : undefined}>
                      <span className="qp-h">
                        {col.label}
                        <ColFilterButton<Qualifier>
                          variant="inline"
                          col={col.key}
                          label={col.label}
                          rows={cascadeRows(searchFiltered, filters, col.key, textOf)}
                          formatter={col.text}
                          value={filters[col.key] ?? null}
                          onChange={(v) => setFilters(p => ({ ...p, [col.key]: v }))}
                        />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map(r => (
                  <tr key={r.qualifierId} className={selected.has(r.symbol) ? 'qp-sel' : ''}
                    onClick={() => toggle(r.symbol)}>
                    <td><input type="checkbox" checked={selected.has(r.symbol)} readOnly /></td>
                    <td>{r.qualifierId}</td>
                    <td>{r.symbol}</td>
                    <td>{r.definition}</td>
                  </tr>
                ))}
                {pageRows.length === 0 && (
                  <tr><td colSpan={4} className="qp-msg">No qualifiers match.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Bottom pagination bar — page count · nav · page-size (ALPACA parity). */}
        <div className="qp-page">
          <span className="qp-pinfo">Page {clampedPage} of {totalPages} ({filtered.length} items)</span>
          <span className="qp-pnav">
            <button disabled={clampedPage <= 1} onClick={() => setPage(clampedPage - 1)} aria-label="Previous">‹</button>
            <span className="qp-pcur">{clampedPage}</span>
            <button disabled={clampedPage >= totalPages} onClick={() => setPage(clampedPage + 1)} aria-label="Next">›</button>
          </span>
          <span className="qp-psz">
            Page size:
            <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
              {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </span>
        </div>

        <div className="qp-ft">
          <span style={{ flex: 1 }} />
          <button className="qp-ok" onClick={accept}>OK</button>
          <button className="qp-cancel" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
